/**
 * FormsSection.tsx — the patient portal's "My forms" section (PATIENT-gated).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 10 (patient
 * portal basis, 2026-07-22).
 *
 * WHY: the patient's read-only view of the questionnaires THEY submitted, with
 * the clinician's PATIENT-VISIBLE notes shown against them. Master → detail:
 *   • the list (GET /v1/clinic/assessments/mine) — one row per submitted form
 *     with its state + eligibility + submit date;
 *   • the detail (GET /v1/clinic/assessments/mine/detail?assessmentId=) — the
 *     answer projection rendered as read-only summary sections (never raw JSON),
 *     with the clinician's notes (GET /v1/clinic/assessments/mine/notes?assessmentId=)
 *     rendered AGAINST the matching answer (by `questionKey`) or as a general note.
 *
 * SELF-SCOPED (R41): every read is `/mine` — the gateway binds the caller to
 * their own patientUserId, so a patient can only ever see their OWN forms and
 * notes (anti-enumeration). Note bodies are server-sanitised; the render
 * applies the same defence-in-depth strip (lib/sanitizeHtml) before handing
 * them to `dangerouslySetInnerHTML`. Every fetch is retryable (TA-06).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyAssessment, getMyAssessmentNotes, getMyAssessments } from '../api/patientForms';
import type { PatientAssessmentDetail, PatientAssessmentSummary, PatientVisibleNote, ReviewSection } from '../api/types';
import { sanitizeNoteHtml } from '../lib/sanitizeHtml';
import { formatUtcDate } from '../lib/format';
import type { PatientStatusCopy } from '../lib/patientStatusCopy';
import { Button, ButtonArrow, buttonClass, buttonStyle, Card, ErrorState, Help, PageFrame, Title, ChevronLeftIcon } from '../ui/kit';
import { IconAudit } from '../ui/icons';
import { PatientStatusBadge } from '../ui/PatientStatusBadge';
import { CardIconHeader, SectionTitle } from '../ui/sections';
import { SkeletonCard } from '../ui/Skeleton';

const STATE_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
};

const ELIGIBILITY_LABEL: Record<string, string> = {
  eligible: 'Eligible',
  clinician_review: 'In clinician review',
  not_eligible: 'Not eligible',
};

/**
 * One readable status pill from the form's state + eligibility (patient-facing —
 * sentence case, one clear outcome). An approval subsumes "eligible"; a
 * clinician review is the meaningful in-flight status.
 */
function formStatusCopy(state: string, eligibility: string): PatientStatusCopy {
  if (state === 'approved') return { label: 'Approved', tone: 'positive' };
  if (state === 'rejected') return { label: 'Not approved', tone: 'danger' };
  if (eligibility === 'not_eligible') return { label: 'Not eligible', tone: 'danger' };
  if (eligibility === 'clinician_review') return { label: 'In clinician review', tone: 'pending' };
  if (eligibility === 'eligible') return { label: 'Eligible', tone: 'positive' };
  if (state === 'draft') return { label: 'Draft', tone: 'muted' };
  return { label: STATE_LABEL[state] ?? state, tone: 'neutral' };
}

export function FormsSection() {
  const [list, setList] = useState<PatientAssessmentSummary[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(() => {
    setStatus('loading');
    getMyAssessments()
      .then((items) => {
        setList(items);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (status === 'loading') {
    return (
      <PageFrame bare>
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
          <SkeletonCard lines={2} label="Loading your forms…" />
          <SkeletonCard lines={2} />
        </div>
      </PageFrame>
    );
  }

  if (status === 'error' || !list) {
    return (
      <PageFrame bare>
        <ErrorState message="Sorry — we could not load your forms." onRetry={load} />
      </PageFrame>
    );
  }

  if (selectedId) {
    return (
      <PageFrame bare>
        <FormDetail assessmentId={selectedId} onBack={() => setSelectedId(null)} />
      </PageFrame>
    );
  }

  return (
    <PageFrame bare>
      <Title as="h2">My forms</Title>
      {list.length === 0 ? (
        <EmptyForms />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--gps-s-4)' }}>
          {list.map((item) => (
            <li key={item.assessmentId}>
              <Card className="gps-card-interactive">
                <CardIconHeader
                  icon={<IconAudit />}
                  title="Eligibility check"
                  subtitle={`Submitted ${formatUtcDate(item.submittedAt) || '—'}`}
                  action={<PatientStatusBadge status={formStatusCopy(item.state, item.eligibility)} />}
                />
                <Button type="button" variant="ghost" onClick={() => setSelectedId(item.assessmentId)}>
                  View form
                </Button>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </PageFrame>
  );
}

/** Warm empty state — mirrors the OrdersSection empty card (icon-free, CTA back
 * into the check) so every patient section shares one empty-state grammar. */
function EmptyForms() {
  return (
    <Card style={{ textAlign: 'center', padding: 'var(--gps-s-6) var(--gps-s-4)' }}>
      <SectionTitle>No forms yet</SectionTitle>
      <Help style={{ maxWidth: '26rem', margin: '0 auto var(--gps-s-4)' }}>
        You haven&rsquo;t submitted any forms yet. When you complete an eligibility check, it will
        appear here.
      </Help>
      <div style={{ maxWidth: '20rem', margin: '0 auto' }}>
        <Link to="/weightloss" className={buttonClass('cta')} style={buttonStyle('cta')}>
          Start your 2-minute check
          <ButtonArrow />
        </Link>
      </div>
    </Card>
  );
}

/** The read-only detail of one of the patient's own forms + its clinician notes. */
function FormDetail({ assessmentId, onBack }: { assessmentId: string; onBack: () => void }) {
  const [detail, setDetail] = useState<PatientAssessmentDetail | null>(null);
  const [notes, setNotes] = useState<PatientVisibleNote[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setStatus('loading');
    Promise.all([getMyAssessment(assessmentId), getMyAssessmentNotes(assessmentId)])
      .then(([form, noteList]) => {
        setDetail(form);
        setNotes(noteList);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [assessmentId]);

  useEffect(() => {
    load();
  }, [load]);

  const backLink = (
    <button
      type="button"
      onClick={onBack}
      style={{
        marginLeft: 'auto',
        minHeight: '44px',
        padding: '0 4px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: 'none',
        border: 0,
        color: 'var(--gps-link)',
        fontSize: '14px',
        cursor: 'pointer',
      }}
    >
      <ChevronLeftIcon size={14} />
      Back to forms
    </button>
  );

  if (status === 'loading') {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
          <Title as="h2">Your form</Title>
          {backLink}
        </div>
        <SkeletonCard lines={5} label="Loading your form…" />
      </>
    );
  }

  if (status === 'error' || !detail) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
          <Title as="h2">Your form</Title>
          {backLink}
        </div>
        <ErrorState message="Sorry — we could not load this form." onRetry={load} />
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
        <Title as="h2">Your form</Title>
        {backLink}
      </div>
      <p style={{ color: 'var(--gps-muted)', fontSize: '13.5px', margin: '0 0 var(--gps-s-4)' }}>
        Submitted {formatUtcDate(detail.submittedAt) || '—'} ·{' '}
        {STATE_LABEL[detail.state] ?? detail.state} · {ELIGIBILITY_LABEL[detail.eligibility] ?? detail.eligibility}
      </p>
      <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
        {detail.sections.map((section) => (
          <FormSectionCard key={section.key} section={section} notes={notes} />
        ))}
        <GeneralNotesCard notes={notes} />
      </div>
    </>
  );
}

/** One projection section, with any clinician notes anchored to its answers. */
function FormSectionCard({ section, notes }: { section: ReviewSection; notes: PatientVisibleNote[] }) {
  // questionKey → the notes anchored to that answer (memoised per note list).
  const byQuestion = useMemo(() => {
    const map = new Map<string, PatientVisibleNote[]>();
    for (const note of notes) {
      if (!note.questionKey) continue;
      const bucket = map.get(note.questionKey) ?? [];
      bucket.push(note);
      map.set(note.questionKey, bucket);
    }
    return map;
  }, [notes]);

  return (
    <Card>
      <SectionTitle>{section.title}</SectionTitle>
      <dl style={{ margin: 0 }}>
        {section.items.map((item) => {
          const anchored = byQuestion.get(item.questionKey) ?? [];
          return (
            <div key={item.questionKey} className="gps-data-row">
              <dt style={{ color: 'var(--gps-muted)', fontSize: '13px' }}>{item.prompt}</dt>
              <dd style={{ margin: '2px 0 0', color: 'var(--gps-text)', fontSize: '14.5px', fontWeight: 600 }}>
                {item.answer}
                {item.detail ? <span style={{ display: 'block', fontWeight: 400, color: 'var(--gps-muted)' }}>{item.detail}</span> : null}
              </dd>
              {anchored.map((note) => (
                <ClinicianNote key={note.noteId} note={note} />
              ))}
            </div>
          );
        })}
      </dl>
    </Card>
  );
}

/** The general (un-anchored) clinician notes — hidden entirely when there are none. */
function GeneralNotesCard({ notes }: { notes: PatientVisibleNote[] }) {
  const general = notes.filter((n) => !n.questionKey);
  if (general.length === 0) return null;
  return (
    <Card>
      <SectionTitle>Notes from your clinician</SectionTitle>
      <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
        {general.map((note) => (
          <ClinicianNote key={note.noteId} note={note} />
        ))}
      </div>
    </Card>
  );
}

/** One patient-visible clinician note — sanitised HTML body + its date. */
function ClinicianNote({ note }: { note: PatientVisibleNote }) {
  return (
    <div
      style={{
        marginTop: 'var(--gps-s-2)',
        padding: '10px 12px',
        background: 'var(--gps-primary-light)',
        borderRadius: 'var(--gps-r-md)',
        fontSize: '13.5px',
        color: 'var(--gps-text)',
      }}
    >
      <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gps-muted)', marginBottom: '3px' }}>
        Clinician note · {formatUtcDate(note.createdAt)}
      </div>
      {/* Defence-in-depth: the server already sanitised this; strip again here. */}
      <div dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.body) }} />
    </div>
  );
}
